import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { MenuBarComponent } from '../../core/components/menu-bar/menu-bar.component';
import { FooterComponent } from '../../core/components/footer/footer.component';
import { EquipmentService, EquipmentSummary } from '../../core/services/equipment.service';

/**
 * The equipment catalogue. Copy here is English-only by design — product codes
 * and technical values are not translated, so no Tolgee keys.
 */
@Component({
    selector: 'app-equipment',
    imports: [FormsModule, MenuBarComponent, FooterComponent],
    templateUrl: './equipment.component.html',
    styleUrl: './equipment.component.scss',
})
export class EquipmentComponent implements OnInit {
    equipment: EquipmentSummary[] = [];
    loading = false;
    failed = false;
    search = '';

    /**
     * Per-thumbnail load tracking. The list endpoint returns 30 signed S3 URLs
     * that the browser fetches after the grid has already rendered, so each
     * tile carries its own skeleton until its image settles. Keyed by id and
     * never cleared, so filtering the grid cannot re-flash a settled tile.
     */
    private readonly settled = new Set<string>();
    private readonly failedImages = new Set<string>();

    constructor(
        private equipmentService: EquipmentService,
        private router: Router,
    ) { }

    ngOnInit(): void {
        window.scrollTo({ top: 0, behavior: 'instant' });
        this.load();
    }

    get filtered(): EquipmentSummary[] {
        const q = this.search.trim().toLowerCase();
        if (!q) return this.equipment;
        return this.equipment.filter((e) =>
            (e.code || '').toLowerCase().includes(q) ||
            (e.name || '').toLowerCase().includes(q),
        );
    }

    load(): void {
        this.loading = true;
        this.failed = false;
        // a re-fetch hands back freshly signed URLs, so the tiles load again
        this.settled.clear();
        this.failedImages.clear();
        this.equipmentService.list().subscribe({
            next: (res) => { this.equipment = res?.equipment ?? []; this.loading = false; },
            error: () => { this.equipment = []; this.loading = false; this.failed = true; },
        });
    }

    public open(item: EquipmentSummary): void {
        this.router.navigate(['/equipment', item.slug]);
    }

    /** Whether to show the skeleton in this tile. */
    imagePending(item: EquipmentSummary): boolean {
        return !!item.image && !this.settled.has(item.id);
    }

    /** A thumbnail that 404s or expires is treated like a product with no photo. */
    imageBroken(item: EquipmentSummary): boolean {
        return this.failedImages.has(item.id);
    }

    onImageLoad(item: EquipmentSummary): void {
        this.settled.add(item.id);
    }

    onImageError(item: EquipmentSummary): void {
        this.settled.add(item.id);
        this.failedImages.add(item.id);
    }
}
